import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Scale, 
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  BookOpen,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const scaleInfo = [
  {
    key: "necessity",
    name: "Necessity",
    question: "Is restriction strictly required to prevent demonstrable material harm?",
    thresholdRule: "Score ≤ 2 → INVALID",
    failThreshold: 2,
    icon: "1"
  },
  {
    key: "proportionality", 
    name: "Proportionality",
    question: "Is restriction narrowly tailored to the harm?",
    thresholdRule: "Score ≤ 2 → INVALID",
    failThreshold: 2,
    icon: "2"
  },
  {
    key: "liberty_impact",
    name: "Liberty Impact",
    question: "How severely does restriction reduce fundamental freedoms?",
    thresholdRule: "Score ≤ 1 → Extreme justification required",
    failThreshold: 1,
    icon: "3"
  },
  {
    key: "reversibility",
    name: "Reversibility",
    question: "Can the harm caused by restriction be undone?",
    thresholdRule: "Score ≤ 1 → INVALID (unless extreme harm)",
    failThreshold: 1,
    icon: "4"
  }
];

const scoringGuide = [
  { score: "0", label: "Fails completely", color: "bg-red-500" },
  { score: "1-2", label: "Weak/Insufficient", color: "bg-orange-500" },
  { score: "3", label: "Borderline", color: "bg-yellow-500" },
  { score: "4-5", label: "Satisfied", color: "bg-green-500" },
];

const verdictStyles = {
  "IMPERMISSIBLE": { color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10 border-red-500/30", icon: XCircle },
  "POTENTIALLY_PERMISSIBLE": { color: "text-green-600 dark:text-green-400", bg: "bg-green-500/10 border-green-500/30", icon: CheckCircle2 },
  "REQUIRES_REVIEW": { color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", icon: AlertTriangle },
  "INSUFFICIENT_EVIDENCE": { color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/30", icon: HelpCircle },
};

function ScoreBar({ score, failThreshold }) {
  const percentage = (score / 5) * 100;
  const passed = score > failThreshold;
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className={passed ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
          {passed ? "PASS" : "FAIL"}
        </span>
        <span className="font-mono font-bold">{score}/5</span>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-500 ${
            score <= 1 ? 'bg-red-500' : 
            score <= 2 ? 'bg-orange-500' : 
            score === 3 ? 'bg-yellow-500' : 
            'bg-green-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>0</span>
        <span className="text-red-500">Threshold: {failThreshold}</span>
        <span>5</span>
      </div>
    </div>
  );
}

export default function FourScalePage() {
  const [situation, setSituation] = useState('');
  const [restriction, setRestriction] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEvaluate = async () => {
    if (!situation.trim() || !restriction.trim()) {
      toast.error('Please describe both the situation and proposed restriction');
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/four-scale/evaluate`, {
        situation: situation.trim(),
        proposed_restriction: restriction.trim()
      });
      setResult(response.data);
    } catch (error) {
      console.error('Evaluation error:', error);
      toast.error('Failed to evaluate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSituation('');
    setRestriction('');
    setResult(null);
  };

  const evaluation = result?.evaluation;
  const hasStructuredData = evaluation && evaluation.scales && !evaluation.parse_error;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="four-scale-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <Scale className="h-4 w-4" />
          <span>Article 22</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Four-Scale Engine v1.0
        </h1>
        <p className="text-muted-foreground">
          Structured evaluation with numeric scoring (0-5) and threshold logic
        </p>
      </div>

      {/* Scoring Guide */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Scoring Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {scoringGuide.map((item) => (
              <div key={item.score} className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded ${item.color}`} />
                <span className="text-sm"><strong>{item.score}</strong>: {item.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Each scale evaluated independently. NO averaging. Any hard failure invalidates the restriction.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          {/* The Four Scales Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">The Four Scales</CardTitle>
              <CardDescription>Each must pass independently — no averaging</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {scaleInfo.map((scale) => (
                <div key={scale.key} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-semibold text-sm flex-shrink-0">
                    {scale.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{scale.name}</h4>
                      <Badge variant="outline" className="text-xs text-red-600 dark:text-red-400">
                        {scale.thresholdRule}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{scale.question}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Input Form */}
          <Card data-testid="four-scale-input">
            <CardHeader>
              <CardTitle className="text-lg">Evaluate a Restriction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Situation / Context</Label>
                <Textarea
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  placeholder="Describe the situation requiring analysis..."
                  className="min-h-[100px]"
                  data-testid="situation-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Proposed Restriction</Label>
                <Textarea
                  value={restriction}
                  onChange={(e) => setRestriction(e.target.value)}
                  placeholder="What restriction is being proposed or evaluated?"
                  className="min-h-[100px]"
                  data-testid="restriction-input"
                />
              </div>

              <div className="flex space-x-2">
                <Button 
                  onClick={handleEvaluate} 
                  disabled={loading || !situation.trim() || !restriction.trim()}
                  className="flex-1"
                  data-testid="evaluate-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Scale className="h-4 w-4 mr-2" />
                      Run Four-Scale Test
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleReset} data-testid="reset-button">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results Section */}
        <div>
          {result ? (
            <Card data-testid="evaluation-results">
              <CardHeader>
                <CardTitle className="text-lg">Evaluation Results</CardTitle>
                <CardDescription>{result.article_reference}</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[700px] pr-4">
                  {hasStructuredData ? (
                    <div className="space-y-6">
                      {/* Verdict Banner */}
                      {evaluation.verdict && (
                        <div className={`p-4 rounded-lg border ${verdictStyles[evaluation.verdict]?.bg || 'bg-muted'}`}>
                          <div className="flex items-center space-x-3">
                            {(() => {
                              const VerdictIcon = verdictStyles[evaluation.verdict]?.icon || AlertCircle;
                              return <VerdictIcon className={`h-6 w-6 ${verdictStyles[evaluation.verdict]?.color}`} />;
                            })()}
                            <div>
                              <h3 className={`font-bold text-lg ${verdictStyles[evaluation.verdict]?.color}`}>
                                {evaluation.verdict.replace(/_/g, ' ')}
                              </h3>
                              <p className="text-sm text-muted-foreground">{evaluation.verdict_reasoning}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Scale Scores */}
                      <div className="space-y-4">
                        <h4 className="font-medium flex items-center">
                          <Scale className="h-4 w-4 mr-2" />
                          Scale Breakdown
                        </h4>
                        
                        {scaleInfo.map((scale) => {
                          const scaleData = evaluation.scales?.[scale.key];
                          if (!scaleData) return null;
                          
                          const passed = scaleData.score > scale.failThreshold;
                          
                          return (
                            <div key={scale.key} className={`p-4 rounded-lg border ${passed ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${passed ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                                    {scale.icon}
                                  </div>
                                  <h5 className="font-medium">{scale.name}</h5>
                                </div>
                                <Badge className={passed ? 'bg-green-500/20 text-green-700' : 'bg-red-500/20 text-red-700'}>
                                  {passed ? 'PASS' : 'FAIL'}
                                </Badge>
                              </div>
                              
                              <ScoreBar score={scaleData.score} failThreshold={scale.failThreshold} />
                              
                              <p className="text-sm mt-3">{scaleData.reasoning}</p>
                              
                              {scaleData.citations?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {scaleData.citations.map((cite, i) => (
                                    <Link key={i} to={`/article/${cite}`} className="text-xs text-accent hover:underline">
                                      Art. {cite}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Threshold Summary */}
                      {evaluation.threshold_analysis && (
                        <div className="p-4 rounded-lg bg-muted/50">
                          <h4 className="font-medium mb-3">Threshold Analysis</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {scaleInfo.map((scale) => {
                              const passed = evaluation.threshold_analysis[`${scale.key}_pass`];
                              return (
                                <div key={scale.key} className="flex items-center space-x-2">
                                  {passed ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                  <span>{scale.name}</span>
                                </div>
                              );
                            })}
                          </div>
                          {evaluation.threshold_analysis.failed_scales?.length > 0 && (
                            <p className="text-sm text-red-600 mt-2">
                              Failed: {evaluation.threshold_analysis.failed_scales.join(', ')}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Citations */}
                      {evaluation.all_citations?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center">
                            <BookOpen className="h-4 w-4 mr-2" />
                            All Citations
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {evaluation.all_citations.map((cite, i) => (
                              <Link key={i} to={`/article/${cite}`}>
                                <Badge variant="outline" className="hover:bg-accent/20">
                                  Article {cite}
                                </Badge>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Self-Limitation */}
                      {evaluation.self_limitation && (
                        <div className="p-3 rounded-lg bg-muted/30 text-xs text-muted-foreground italic">
                          {evaluation.self_limitation}
                        </div>
                      )}

                      {/* Appeal Option */}
                      <Card className="border-accent/30 bg-accent/5">
                        <CardContent className="pt-4">
                          <div className="flex items-start space-x-3">
                            <AlertTriangle className="h-5 w-5 text-accent mt-0.5" />
                            <div>
                              <h4 className="font-medium">Appeal to Independent Review</h4>
                              <p className="text-sm text-muted-foreground">
                                Per Article 4 and Article 11, any restriction determination can be 
                                challenged through independent review. This is an educational tool — 
                                actual cases require proper adjudication.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    // Fallback for non-structured response
                    <div className="space-y-4">
                      <div className="prose dark:prose-invert max-w-none text-sm whitespace-pre-wrap">
                        {evaluation?.raw_response || JSON.stringify(evaluation, null, 2)}
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full min-h-[400px] flex items-center justify-center">
              <CardContent className="text-center text-muted-foreground">
                <Scale className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-2">Ready to Evaluate</h3>
                <p className="text-sm max-w-xs mx-auto">
                  Enter a situation and proposed restriction, then run the 
                  Four-Scale Test to get structured scoring with threshold analysis.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Critical Rule */}
      <Card className="mt-8 border-red-500/30 bg-red-500/5">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <h4 className="font-medium">No Averaging — Each Scale Stands Alone</h4>
              <p className="text-sm text-muted-foreground">
                Scores are NOT averaged. A restriction scoring 5+5+5+0 FAILS because reversibility failed.
                This prevents gaming the system. Each threshold is a hard gate.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
